import { PdfAnnotationAction } from "@/Documents/Models/UI/PdfAnnotationActions";
import { formatAnnotationStyleSize, highlightAnnotationStrokeSizes, pencilAnnotationStrokeSizes, textAnnotationFontSizes } from "./Settings/AnnotationSettings";
import { createSignatureTool } from "./Tools/SignatureTool";
import { createStampCrossCheckTool } from "./Tools/StampCrossCheckTool";
import { createStampTextTool } from "./Tools/StampTextTool";

/** A helper class to manage annotations. */
class AnnotationHelper {
    /**
     * Add custom tools and styles.
     * @param documentViewer The current document viewer
     */
    public static async applyDocumentViewerCustomToolsAndStyles(documentViewer: Core.DocumentViewer): Promise<void> {
        await createStampCrossCheckTool(documentViewer);
        await createStampTextTool(documentViewer);
        await createSignatureTool(documentViewer);

        documentViewer.getTool(Core.Tools.ToolNames.FREETEXT).setStyles(this.getDefaultAnnotationsStyles(PdfAnnotationAction.Text));
        documentViewer.getTool(Core.Tools.ToolNames.FREEHAND).setStyles(this.getDefaultAnnotationsStyles(PdfAnnotationAction.Pencil));
        documentViewer.getTool(Core.Tools.ToolNames.RECTANGLE).setStyles(this.getDefaultAnnotationsStyles(PdfAnnotationAction.Rectangle));
        documentViewer.getTool(Core.Tools.ToolNames.FREEHAND_HIGHLIGHT).setStyles(this.getDefaultAnnotationsStyles(PdfAnnotationAction.Highlight));
    }

    /**
     * Relate enum with PdfTron tool's name.
     * @param action The current pdf action enum
     */
    public static getPdfTronToolName(action: PdfAnnotationAction): Core.Tools.ToolNames {
        switch (action) {
            case PdfAnnotationAction.Text:
                return Core.Tools.ToolNames.FREETEXT;
            case PdfAnnotationAction.Pencil:
                return Core.Tools.ToolNames.FREEHAND;
            case PdfAnnotationAction.Highlight:
                return Core.Tools.ToolNames.FREEHAND_HIGHLIGHT;
            default:
                break;
        }
    }

    private static getDefaultAnnotationsStyles(action: PdfAnnotationAction): object {
        switch (action) {
            case PdfAnnotationAction.Text:
                const fontDefaultSize = textAnnotationFontSizes.firstOrDefault(p => p.isDefault);
                return ({
                    TextColor: new Core.Annotations.Color(10, 91, 173), // #0A5BAD (blue)
                    Font: "Candara",
                    FontSize: formatAnnotationStyleSize(fontDefaultSize),
                    RichTextStyle: { 0: { "font-weight": "bold" } }
                });

            case PdfAnnotationAction.Pencil:
                const pencilDefaultSize = pencilAnnotationStrokeSizes.firstOrDefault(p => p.isDefault);
                return ({
                    StrokeColor: new Core.Annotations.Color(0, 112, 210), // #0070D2 (blue)
                    StrokeThickness: formatAnnotationStyleSize(pencilDefaultSize)
                });

            case PdfAnnotationAction.Rectangle:
                return ({
                    StrokeColor: new Core.Annotations.Color(0, 112, 210), // #0070D2 (blue)
                    StrokeThickness: "2px"
                });

            case PdfAnnotationAction.Highlight:
                const highlightDefaultSize = highlightAnnotationStrokeSizes.firstOrDefault(p => p.isDefault);
                return ({
                    StrokeColor: new Core.Annotations.Color(255, 230, 0), // #FFE600 (yellow)
                    Opacity: "0.4",
                    StrokeThickness: formatAnnotationStyleSize(highlightDefaultSize)
                });
        }
    }
}

export { AnnotationHelper };
